import { createFileRoute, notFound } from "@tanstack/react-router";
import { PageTemplate } from "@/components/site/PageTemplate";
import { problems } from "@/content/problems";
import boardroomAssetSrc from "@/assets/boardroom.png";
const boardroomAsset = { url: boardroomAssetSrc };
import supervisorAssetSrc from "@/assets/supervisor.png";
const supervisorAsset = { url: supervisorAssetSrc };
import resignAssetSrc from "@/assets/resign.jpg";
const resignAsset = { url: resignAssetSrc };
import consistentHeroAssetSrc from "@/assets/prob-consistent-hero.jpg";
const consistentHeroAsset = { url: consistentHeroAssetSrc };
import consistentCostAssetSrc from "@/assets/prob-consistent-cost.jpg";
const consistentCostAsset = { url: consistentCostAssetSrc };
import accountabilityHeroAssetSrc from "@/assets/prob-accountability-hero.jpg";
const accountabilityHeroAsset = { url: accountabilityHeroAssetSrc };
import accountabilityCostAssetSrc from "@/assets/prob-accountability-cost.jpg";
const accountabilityCostAsset = { url: accountabilityCostAssetSrc };
import conversationsHeroAssetSrc from "@/assets/prob-conversations-hero.jpg";
const conversationsHeroAsset = { url: conversationsHeroAssetSrc };
import conversationsCostAssetSrc from "@/assets/prob-conversations-cost.jpg";
const conversationsCostAsset = { url: conversationsCostAssetSrc };
import changeHeroAssetSrc from "@/assets/prob-change-hero.jpg";
const changeHeroAsset = { url: changeHeroAssetSrc };
import changeCostAssetSrc from "@/assets/prob-change-cost-2.jpg";
const changeCostAsset = { url: changeCostAssetSrc };
import leanHrHeroAssetSrc from "@/assets/prob-lean-hr-hero.jpg";
const leanHrHeroAsset = { url: leanHrHeroAssetSrc };
import leanHrCostAssetSrc from "@/assets/prob-lean-hr-cost.jpg";
const leanHrCostAsset = { url: leanHrCostAssetSrc };
import supervisorsHeroAssetSrc from "@/assets/prob-supervisors-hero-2.jpg";
import { SITE_URL } from "@/lib/seo";
const supervisorsHeroAsset = { url: supervisorsHeroAssetSrc };

const HERO_BACKGROUNDS: Record<string, string> = {
  "creating-consistent-leadership": consistentHeroAsset.url,
  "building-accountability": accountabilityHeroAsset.url,
  "improving-difficult-conversations": conversationsHeroAsset.url,
  "leading-through-change": changeHeroAsset.url,
  "scaling-leadership-development-lean-hr": leanHrHeroAsset.url,
};

const HERO_POSITIONS: Record<string, string> = {
  "creating-consistent-leadership": "center 30%",
  "building-accountability": "center 35%",
  "improving-difficult-conversations": "center 35%",
  "leading-through-change": "center center",
  "scaling-leadership-development-lean-hr": "center 25%",
  "preparing-new-supervisors": "center calc(12% - 100px)",
};

export const Route = createFileRoute("/problems/$slug")({
  loader: ({ params }) => {
    const page = problems.find((p) => p.slug === params.slug);
    if (!page) throw notFound();
    return page;
  },
  head: ({ loaderData, params }) => {
    if (!loaderData)
      return { meta: [{ title: "Not found" }, { name: "robots", content: "noindex" }] };
    return {
      links: [{ rel: "canonical", href: `${SITE_URL}/problems/${params.slug}/` }],
      meta: [
        { title: loaderData.title },
        { name: "description", content: loaderData.metaDescription },
        { property: "og:title", content: loaderData.title },
        { property: "og:description", content: loaderData.metaDescription },
      ],
    };
  },
  component: ProblemPage,
});

const IMPACT_STATS: Record<
  string,
  {
    stat: string;
    body: string;
    source: string;
    image?: string;
    alt?: string;
    imagePosition?: string;
  }
> = {
  "building-accountability": {
    stat: "52%",
    body: "of employees who voluntarily leave say their manager or organization could have prevented it.",
    source: "Gallup, 2019",
    image: accountabilityCostAsset.url,
    alt: "Five colleagues standing in an office reviewing documents and a laptop together",
    imagePosition: "center 35%",
  },
  "improving-difficult-conversations": {
    stat: "51%",
    body: "of exiting employees say no manager spoke with them about their job satisfaction in their final three months.",
    source: "Gallup, 2019",
    image: conversationsCostAsset.url,
    alt: "A woman using a tablet beside chalk-drawn satisfaction ratings on a wall",
    imagePosition: "center 40%",
  },
  "creating-consistent-leadership": {
    stat: "70%",
    body: "Managers account for 70% of the variance in their own team's engagement.",
    source: "Gallup, 2015",
    image: consistentCostAsset.url,
    alt: "A team of business professionals sharing a group high-five in a bright office",
    imagePosition: "center 85%",
  },
  "leading-through-change": {
    stat: "7 in 10",
    body: "U.S. workers reported disruptive change at their organization in the past year — leaders and managers absorbed the most of it.",
    source: "Gallup, 2025",
    image: changeCostAsset.url,
    alt: "Construction crew in hard hats and safety vests working at a street excavation site",
    imagePosition: "center center",
  },
  "scaling-leadership-development-lean-hr": {
    stat: "30% → 27%",
    body: "Manager engagement fell in a single year, driving most of the global drop in employee engagement.",
    source: "Gallup, 2026",
    image: leanHrCostAsset.url,
    alt: "Two business leaders shaking hands across a conference table while colleagues look on",
    imagePosition: "center 45%",
  },
};

function ProblemPage() {
  const page = Route.useLoaderData();
  const isSupervisors = page.slug === "preparing-new-supervisors";
  const heroBg = isSupervisors ? supervisorsHeroAsset.url : HERO_BACKGROUNDS[page.slug];
  const placeholderStat = IMPACT_STATS[page.slug];
  return (
    <PageTemplate
      page={page}
      symptomsVariant="list"
      {...(heroBg ? { heroBackgroundImage: heroBg } : { heroSolidPurple: true })}
      {...(heroBg && HERO_POSITIONS[page.slug]
        ? { heroBackgroundPosition: HERO_POSITIONS[page.slug] }
        : {})}
      approachBackgroundImage={isSupervisors ? supervisorAsset.url : boardroomAsset.url}
      finalBackgroundImage={isSupervisors ? supervisorAsset.url : boardroomAsset.url}
      {...(isSupervisors
        ? {
            impactStat: {
              image: resignAsset.url,
              alt: "A manager packing personal belongings into a cardboard box at her desk",
              stat: "50%",
              body: "Half of new managers quit within 18 months when nobody prepares them for the job.",
            },
          }
        : placeholderStat
          ? {
              impactStat: placeholderStat.image
                ? placeholderStat
                : { ...placeholderStat, placeholder: true },
            }
          : {})}
    />
  );
}
